/**
 * One config table, for any of the five - and for the sixth nobody has asked for yet.
 *
 * Roman's requirement for the settings tab was that a further setting later is a registry line
 * and not a new component. That only holds if there is exactly ONE table renderer, so this is
 * it: it takes the field contract the server shipped, the column metadata from the section
 * registry, and the rows, and renders them. Nothing in here knows what a Kennzahl is.
 *
 * Table standard as everywhere else on this page: sticky header, chevron sorting, draggable
 * column widths, and Phase 1's Excel-style column filter - with the footer count and the
 * "x of y" both following the FILTERED set, because a footer that counts something other than
 * the visible rows is worse than no footer.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Pencil, Trash2, Copy, Plus } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { useVatLang } from './VatLang';
import type {
    ConfigFieldContract, ConfigResourceContract, ConfigRow, ConfigRowValue,
} from '../../types/vatReporting';

export interface ConfigColumnMeta {
    /** Must match a field name in the resource contract. */
    name: string;
    labelKey: string;
    width: number;
    align?: 'right';
    /** Cell override. Gets the raw row so it can combine two fields. */
    render?: (row: ConfigRow, helpers: { text: (v: ConfigRowValue) => ReactNode }) => ReactNode;
}

interface Props {
    contract: ConfigResourceContract;
    columns: ConfigColumnMeta[];
    rows: ConfigRow[];
    /** Stable per-table key so a dragged column width outlives the session. */
    widthStorageKey: string;
    canEdit: boolean;
    onCreate: () => void;
    onEdit: (row: ConfigRow) => void;
    onDuplicate: (row: ConfigRow) => void;
    onDelete: (row: ConfigRow) => void;
    /** Extra classes for a row that deserves attention - a pending mapping, typically. */
    rowClass?: (row: ConfigRow) => string | undefined;
}

/** The primary key of a row, as a single string. Used as the React key and for equality. */
export function configRowKey(contract: ConfigResourceContract, row: ConfigRow): string {
    return contract.fields
        .filter(f => f.key)
        .map(f => `${f.name}=${String(row[f.name] ?? '')}`)
        .join('|');
}

function isNumericField(f: ConfigFieldContract | undefined): boolean {
    if (!f) return false;
    if (f.kind === 'number') return true;
    // `sign` is an enum of 1 / -1 - a number in every way that matters to a range filter.
    return f.kind === 'enum' && !!f.values && f.values.every(v => typeof v === 'number');
}

function compareValues(a: ConfigRowValue, b: ConfigRowValue): number {
    // Nulls sort last in both directions; the caller flips the rest.
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
    return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function ConfigTable({
    contract, columns, rows, widthStorageKey, canEdit,
    onCreate, onEdit, onDuplicate, onDelete, rowClass,
}: Props) {
    const { t } = useVatLang();
    const [sort, setSort] = useState<SortState<string>>({ key: null, direction: 'asc' });

    const fieldByName = useMemo(
        () => new Map(contract.fields.map(f => [f.name, f])),
        [contract]);

    const resizableColumns = useMemo(
        () => columns.map(c => ({ key: c.name, width: c.width })),
        [columns]);

    const filterColumns = useMemo(
        () => columns.map(c => ({
            key: c.name,
            accessor: (row: ConfigRow) => {
                const v = row[c.name];
                // The filter list works on strings; a boolean has to read as something a human
                // recognises in the checkbox list, not as "true"/"false".
                if (typeof v === 'boolean') return v ? t('config.yes') : t('config.no');
                return v ?? '';
            },
            numeric: isNumericField(fieldByName.get(c.name)),
        })),
        [columns, fieldByName, t]);

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(resizableColumns, widthStorageKey);
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, filterColumns);

    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    const onSort = (key: string) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, isNumericField(fieldByName.get(key))));
    };

    const sorted = useMemo(() => {
        if (!sort.key) return filtered;
        const key = sort.key;
        const factor = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const c = compareValues(a[key] ?? null, b[key] ?? null);
            // Null always last, so the flip must not carry it to the top.
            if (a[key] === null || a[key] === undefined) return 1;
            if (b[key] === null || b[key] === undefined) return -1;
            return c * factor;
        });
    }, [filtered, sort]);

    /** A cell's default rendering: an empty string is a value here, so it has to be visible. */
    const text = (v: ConfigRowValue): ReactNode => {
        if (v === null || v === undefined) return <span className="text-gray-400">—</span>;
        if (typeof v === 'boolean') {
            return v ? <span className="text-gray-700">{t('config.yes')}</span>
                     : <span className="text-gray-400">{t('config.no')}</span>;
        }
        if (typeof v === 'number') return <span className="tabular-nums">{v}</span>;
        if (v === '') return <span className="text-gray-400 italic">{t('config.emptyValue')}</span>;
        return v;
    };

    return (
        <div className="border rounded bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-2 border-b">
                <div className="text-sm text-gray-600">
                    {activeCount > 0
                        ? t('config.filteredCount', { shown: filtered.length, total: rows.length })
                        : t('config.rowCount', { count: rows.length })}
                </div>
                {canEdit && (
                    <button onClick={onCreate}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
                        <Plus className="h-4 w-4" />{t('config.new')}
                    </button>
                )}
            </div>

            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{t('filter.active', { count: activeCount })}</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                        {t('filter.clearAll')}
                    </button>
                </div>
            )}

            {rows.length === 0 && (
                <div className="p-8 text-center text-gray-500 text-sm">{t('config.empty')}</div>
            )}
            {rows.length > 0 && filtered.length === 0 && (
                <div className="p-8 text-center text-gray-500 text-sm">
                    {t('filter.active', { count: activeCount })}
                </div>
            )}

            {filtered.length > 0 && (
                // The sticky header needs a bounded scroll container or it does not stick.
                <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {columns.map(c => <col key={c.name} style={{ width: widthOf(c.name) }} />)}
                            <col style={{ width: 110 }} />
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {columns.map(c => (
                                    <SortableHeader
                                        key={c.name}
                                        label={t(c.labelKey)}
                                        sortKey={c.name}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.name, x)}
                                        onResetWidth={() => resetWidth(c.name)}
                                        isResizing={resizingKey === c.name}
                                        filter={
                                            <ColumnFilter
                                                label={t(c.labelKey)}
                                                values={uniqueValuesFor(c.name)}
                                                state={filters[c.name]}
                                                onChange={s => setColumnFilter(c.name, s)}
                                                numeric={isNumericField(fieldByName.get(c.name))}
                                                active={isFiltered(c.name)}
                                                labels={filterLabels}
                                            />
                                        }
                                    />
                                ))}
                                <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right text-xs font-medium text-gray-500">
                                    {t('config.actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(row => (
                                <tr key={configRowKey(contract, row)}
                                    className={rowClass?.(row) ?? 'hover:bg-gray-50'}>
                                    {columns.map(c => (
                                        <td key={c.name}
                                            className={`px-3 py-1.5 truncate ${c.align === 'right' ? 'text-right' : ''}`}
                                            title={row[c.name] === null || row[c.name] === undefined
                                                ? undefined : String(row[c.name])}>
                                            {c.render ? c.render(row, { text }) : text(row[c.name] ?? null)}
                                        </td>
                                    ))}
                                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                        {canEdit ? (
                                            <span className="inline-flex items-center gap-1">
                                                <button onClick={() => onEdit(row)} title={t('config.edit')}
                                                        className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                {/* A key change is a delete plus a create, so "duplicate"
                                                    is the honest way to offer moving a rule to another
                                                    combination. */}
                                                <button onClick={() => onDuplicate(row)} title={t('config.duplicate')}
                                                        className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                                                    <Copy className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => onDelete(row)} title={t('config.delete')}
                                                        className="p-1 rounded text-gray-500 hover:bg-red-100 hover:text-red-700">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </span>
                                        ) : (
                                            <span className="text-gray-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
